import { useContext, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Fade,
  FormControlLabel,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ArrowDropDown, ArrowRight } from '@mui/icons-material';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { decryptURL } from 'utils/functions';
import { DropsContext } from 'contexts/DropsContext';
import { toastWarning } from 'utils/toast';
import Card from 'components/Card';
import Input from 'components/Input';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import VirtualDrop from 'components/VirtualDrop';
import styles from './styles';
import api from 'services/api';

const findCategoriaById = (categorias, id) => {
  for (const categoria of categorias) {
    if (categoria?.category_id === id) return categoria;
    if (categoria?.children) {
      const found = findCategoriaById(categoria?.children, id);
      if (found) return found;
    }
  }
  return null;
};

const Shopee = ({ id, integra_access_id, Header }) => {
  const steps = [
    'Identificar Produto',
    'Selecionar Imagens',
    'Escolher Categoria',
    'Selecionar Marca',
    'Adicionais',
    'Envio',
  ];
  const defaultValues = {
    produto_id: null,
    images: [],
    category_id: null,
    marca: null,
    is_mandatory: false,
    adicionais: {},
    envios: [],
  };
  const { hash } = useParams();
  const navigate = useNavigate();
  const decrypt = hash ? decryptURL(hash) : null;
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [imagens, setImagens] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [atributos, setAtributos] = useState([]);
  const [envios, setEnvios] = useState([]);
  const { drops } = useContext(DropsContext);
  const { control, handleSubmit, setValue, watch } = useForm({ defaultValues });
  const category_id = watch('category_id');
  const produto_id = watch('produto_id');
  const _envios = watch('envios');
  const images = watch('images');
  const listCols = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  useEffect(() => {
    if (decrypt?.produto_id) {
      setValue('produto_id', decrypt?.produto_id);
      setTab(1);
    }
  }, []);

  useEffect(() => {
    if (tab === 0) {
      setValue('images', []);
    }
    if (tab === 1) {
      setValue('category_id', null);
      loadImagens();
    }
    if (tab === 2) {
      setValue('marca', null);
      setValue('is_mandatory', false);
    }
    if (tab === 3) {
      setValue('adicionais', {});
      loadMarcas();
    }
    if (tab === 4) {
      setValue('envios', []);
      loadEnvios();
    }
  }, [tab]);

  const onBack = (cb) => {
    if (tab === 0) {
      if (cb) cb();
    } else {
      setTab((prev) => prev - 1);
    }
  };

  const loadImagens = async () => {
    const params = { produto_id };
    try {
      setLoading(true);
      const { data } = await api.get('/Integracoes/Shopee/Imagens', { params });
      setImagens(data?.imagens || []);
      setCategorias(data?.categorias || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadMarcas = async () => {
    const params = { category_id };
    try {
      setLoading(true);
      const { data } = await api.get('/Integracoes/Shopee/Marcas', { params });
      setMarcas(data?.brand_list || []);
      setValue('is_mandatory', data?.is_mandatory);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadEnvios = async () => {
    const params = { category_id };
    try {
      setLoading(true);
      const { data } = await api.get('/Integracoes/Shopee/AtributosEnvios', {
        params,
      });
      setAtributos(data?.atributos || []);
      setEnvios(data?.formasEnvio || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onSelectCategoria = (id) => {
    const selected = findCategoriaById(categorias, id);
    if (selected?.children?.length <= 0) {
      setValue('category_id', id);
      handleSubmit(onSubmit)();
    }
  };

  const onSubmit = async (values) => {
    if (tab === 0) {
      if (!Boolean(values?.produto_id)) {
        return toastWarning('Produto obrigatório');
      }
      setTab(1);
    }
    if (tab === 1) {
      if (!Boolean(values?.images?.length)) {
        return toastWarning('Selecione ao menos uma imagem');
      }
      setTab(2);
    }
    if (tab === 2) {
      if (!Boolean(values?.category_id)) {
        return toastWarning('Selecione uma categoria');
      }
      setTab(3);
    }
    if (tab === 3) {
      if (Boolean(values?.is_mandatory) && !Boolean(values?.marca)) {
        return toastWarning('Selecione uma marca');
      }
      setTab(4);
    }
    if (tab === 4) {
      setTab(5);
    }
    if (tab === 5) {
      if (!Boolean(values?.envios?.length)) {
        return toastWarning('Selecione uma forma de envio');
      }
      try {
        setLoading(true);
        const brand = marcas?.find((f) => f?.brand_id === values?.marca);

        const attribute_list = Object.entries(values?.adicionais).map(
          ([key, value]) => {
            const attribute_value_list = [];
            if (Array.isArray(value?.value_id) && value?.value_id?.length) {
              value?.value_id?.map((v) => {
                attribute_value_list?.push({
                  value_id: v || 0,
                  ...(Boolean(value?.value_unit) && {
                    value_unit: value?.value_unit,
                  }),
                  ...(Boolean(value?.original_name_value) && {
                    original_name_value: value?.original_name_value,
                  }),
                });
              });
            } else if (
              Boolean(value?.original_value_name) ||
              Boolean(value?.value_id)
            ) {
              attribute_value_list?.push({
                value_id: value?.value_id || 0,
                ...(Boolean(value?.value_unit) && {
                  value_unit: value?.value_unit,
                }),
                ...(Boolean(value?.original_name_value) && {
                  original_name_value: value?.original_name_value,
                }),
              });
            }
            return { attribute_id: Number(key), attribute_value_list };
          }
        );

        const data = {
          integra_access_id,
          produto_id: values?.produto_id,
          category_id: values?.category_id,
          imagens: values?.images,
          brand,
          logistic_info: values?.envios,
          attribute_list,
        };

        await api.post(`/Integracoes/Shopee/Anuncio`, data);

        navigate(-1);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
  };

  const renderTree = (nodes) => {
    const child = Array.isArray(nodes?.children) && nodes?.children?.length > 0;
    return (
      <TreeItem
        key={nodes?.category_id?.toString()}
        nodeId={nodes?.category_id?.toString()}
        label={
          <Typography
            py={1}
            variant={child ? 'h6' : 'body1'}
            fontWeight={child ? 'bold' : 'normal'}
          >
            {nodes?.display_category_name}
          </Typography>
        }
      >
        {child ? nodes?.children?.map(renderTree) : null}
      </TreeItem>
    );
  };

  const RenderInputs = ({ input }) => {
    if ([1, 2]?.includes(input?.attribute_info?.input_type)) {
      return (
        <Grid item xs={12} display="flex" alignItems="center" gap={2}>
          <Box flex={3}>
            <Dropdown
              control={control}
              label={input?.label}
              options={input?.values}
              name={`adicionais.${input?.attribute_id}.value_id`}
            />
          </Box>
          {input?.attribute_info?.format_type === 2 && (
            <Box flex={1}>
              <Dropdown
                label="Unidade"
                control={control}
                name={`adicionais.${input?.attribute_id}.value_unit`}
                options={input?.attribute_info?.attribute_unit_list?.map(
                  (v) => ({
                    value: v,
                    label: v,
                  })
                )}
              />
            </Box>
          )}
        </Grid>
      );
    }
    if (input?.attribute_info?.input_type === 3) {
      return (
        <Grid item xs={12} display="flex" alignItems="center" gap={2}>
          <Box flex={3}>
            <Input
              control={control}
              label={input?.label}
              name={`adicionais.${input?.attribute_id}.original_value_name`}
            />
          </Box>
          {input?.attribute_info?.format_type === 2 && (
            <Box flex={1}>
              <Dropdown
                label="Unidade"
                control={control}
                name={`adicionais.${input?.attribute_id}.value_unit`}
                options={input?.attribute_info?.attribute_unit_list?.map(
                  (v) => ({
                    value: v,
                    label: v,
                  })
                )}
              />
            </Box>
          )}
        </Grid>
      );
    }
    if ([4, 5]?.includes(input?.attribute_info?.input_type)) {
      return (
        <Grid item xs={12} display="flex" alignItems="center" gap={2}>
          <Box flex={3}>
            <Dropdown
              multiple
              control={control}
              label={input?.label}
              options={input?.values}
              name={`adicionais.${input?.attribute_id}.value_id`}
            />
          </Box>
          {input?.attribute_info?.format_type === 2 && (
            <Box flex={1}>
              <Dropdown
                label="Unidade"
                control={control}
                name={`adicionais.${input?.attribute_id}.value_unit`}
                options={input?.attribute_info?.attribute_unit_list?.map(
                  (v) => ({
                    value: v,
                    label: v,
                  })
                )}
              />
            </Box>
          )}
        </Grid>
      );
    }
    return null;
  };

  const RenderEnvios = ({ envio, index }) => {
    const checked = _envios?.some(
      (s) => s?.logistic_id === envio?.logistics_channel_id
    );

    const onChange = () => {
      if (checked) {
        setValue(
          'envios',
          _envios?.filter((f) => f?.logistic_id !== envio?.logistics_channel_id)
        );
      } else {
        setValue('envios', [
          ..._envios,
          { logistic_id: envio?.logistics_channel_id, is_free: false },
        ]);
      }
    };

    const updateEnvioField = (logistic_id, field, value) => {
      const updated = _envios?.map((item) =>
        item?.logistic_id === logistic_id ? { ...item, [field]: value } : item
      );
      setValue('envios', updated);
    };

    const current = _envios?.find(
      (e) => e?.logistic_id === envio?.logistics_channel_id
    );

    return (
      <Accordion expanded={checked}>
        <AccordionSummary>
          <Box>
            <FormControlLabel
              label={
                <Typography fontWeight="bold">
                  {envio?.logistics_channel_name}
                </Typography>
              }
              control={
                <Checkbox checked={checked} onChange={onChange} size="small" />
              }
            />
            <Typography
              variant="caption"
              component="div"
              dangerouslySetInnerHTML={{
                __html: envio?.logistics_description || '',
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {checked && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  label="Frete grátis"
                  control={
                    <Checkbox
                      size="small"
                      checked={current?.is_free || false}
                      onChange={(_, v) =>
                        updateEnvioField(
                          envio?.logistics_channel_id,
                          'is_free',
                          v
                        )
                      }
                    />
                  }
                />
              </Grid>
              {envio?.fee_type === 'SIZE_SELECTION' && (
                <Grid item xs={12}>
                  <Dropdown
                    label="Tamanho"
                    control={control}
                    name={`envios[${index}].size_id`}
                    options={envio?.size_list?.map((s) => ({
                      value: s?.size_id,
                      label: s?.default_name,
                      Preço: (s?.default_price || 0)?.toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      }),
                    }))}
                  />
                </Grid>
              )}
              {envio?.fee_type === 'CUSTOM_PRICE' && (
                <Grid item xs={12}>
                  <InputMask
                    label="Preço"
                    control={control}
                    name={`envios[${index}].shipping_fee`}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const RenderSteps = () => {
    if (tab === 0) {
      return (
        <Grid item xs={12}>
          <Card title="Identificar Produto">
            <VirtualDrop
              name="produto_id"
              control={control}
              label="Produto"
              options={drops?.Produto?.filter((f) =>
                [
                  '00-Mercadoria para revenda',
                  '04-Produto acabado',
                  '05-Subproduto',
                  '06-Produto intermediário',
                ]?.includes(f?.tipolinha)
              )}
              disabled={Boolean(id)}
            />
          </Card>
        </Grid>
      );
    }
    if (tab === 1) {
      return (
        <Grid item xs={12}>
          <Card title="Selecionar Imagens">
            <ImageList cols={listCols ? 2 : 5} gap={10}>
              {imagens?.map((i) => {
                const checked = images?.some((s) => s?.id === i?.id);
                return (
                  <ImageListItem key={i?.id}>
                    <Box
                      loading="lazy"
                      component="img"
                      sx={styles?.image}
                      srcSet={`${i.url}?fit=crop&auto=format&dpr=2 2x`}
                      src={`${i.url}?fit=crop&auto=format`}
                    />
                    <ImageListItemBar
                      sx={styles?.bar}
                      position="top"
                      actionPosition="left"
                      actionIcon={
                        <Checkbox
                          checked={checked}
                          sx={styles?.checkbox}
                          onChange={(_, v) => {
                            let arr = [...images];
                            if (v) {
                              arr = [...arr, i];
                            } else {
                              arr = arr?.filter((f) => f?.id !== i?.id);
                            }
                            setValue('images', arr);
                          }}
                        />
                      }
                    />
                  </ImageListItem>
                );
              })}
            </ImageList>
          </Card>
        </Grid>
      );
    }
    if (tab === 2) {
      return (
        <Grid item xs={12}>
          <Card title="Escolher Categoria">
            <TreeView
              onNodeSelect={(_, nodeId) => onSelectCategoria(Number(nodeId))}
              defaultCollapseIcon={<ArrowDropDown />}
              defaultExpandIcon={<ArrowRight />}
            >
              {categorias?.map(renderTree)}
            </TreeView>
          </Card>
        </Grid>
      );
    }
    if (tab === 3) {
      return (
        <Grid item xs={12}>
          <Card title="Selecionar Marca">
            <VirtualDrop
              name="marca"
              control={control}
              label="Marca"
              options={marcas?.map((m) => ({
                label: m?.display_brand_name,
                value: m?.brand_id,
              }))}
            />
          </Card>
        </Grid>
      );
    }
    if (tab === 4) {
      return (
        <Grid item xs={12}>
          <Card title="Adicionais">
            <Grid container spacing={2}>
              {atributos?.map((a, i) => (
                <RenderInputs key={i?.toString()} input={a} />
              ))}
            </Grid>
          </Card>
        </Grid>
      );
    }
    if (tab === 5) {
      return (
        <Grid item xs={12}>
          <Card title="Envio">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {envios?.map((e, i) => (
                  <RenderEnvios key={i?.toString()} envio={e} index={i} />
                ))}
              </Grid>
            </Grid>
          </Card>
        </Grid>
      );
    }
    return null;
  };

  return (
    <Container>
      <Header onBack={onBack} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <Stepper activeStep={tab} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Card>
        </Grid>
        <RenderSteps />
        <Grid
          item
          xs={12}
          textAlign="center"
          display={tab === 2 ? 'none' : 'block'}
        >
          <Button
            color="primary"
            variant="contained"
            loading={loading}
            sx={styles?.btn}
            onClick={handleSubmit(onSubmit)}
          >
            {tab === 5 ? 'Salvar' : 'Próximo'}
          </Button>
        </Grid>
      </Grid>
      <Fade in={loading}>
        <Backdrop open={loading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default Shopee;
